import { Testimonial, api } from '@common';
import { useAuth } from '@hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

// Interface for creating a new testimonial
type NewTestimonialInput = {
  name: string;
  description?: string;
  text: string;
  stars?: number;
  avatar?: File;
  isActive?: boolean;
};

// Interface for updating a testimonial
type UpdateTestimonialInput = Partial<NewTestimonialInput>;

// Interface for testimonial query parameters
interface TestimonialsParams {
  page?: number;
  limit?: number;
  search?: string;
  stars?: number;
  isActive?: boolean;
  orderBy?: string;
  orderType?: 'asc' | 'desc';
}

// Interface for testimonial statistics
interface TestimonialStats {
  total: number;
  active: number;
  archived: number;
  recentTestimonials: number;
  starsDistribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
}

// Interface for paginated response
interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

export const useTestimonials = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Fetch all testimonials with optional parameters
  const testimonials = useQuery<Testimonial[]>({
    queryKey: ['testimonials'],
    queryFn: async () => {
      try {
        const response = await api.get('/testimonials');
        // Handle both array response and paginated response
        if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          return response.data.data;
        }
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        message.error('Error retrieving testimonials');
        throw error;
      }
    }
  });

  // Fetch testimonials with filters and pagination
  const getTestimonialsWithFilters = (params: TestimonialsParams) => {
    return useQuery<PaginatedResponse<Testimonial>>({
      queryKey: ['testimonials', params],
      queryFn: async () => {
        try {
          const response = await api.get('/testimonials', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving testimonials');
          throw error;
        }
      }
    });
  };

  // Fetch active testimonials
  const getActiveTestimonials = (params?: Omit<TestimonialsParams, 'isActive'>) => {
    return useQuery<PaginatedResponse<Testimonial>>({
      queryKey: ['testimonials', 'active', params],
      queryFn: async () => {
        try {
          const response = await api.get('/testimonials/active', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving active testimonials');
          throw error;
        }
      }
    });
  };

  // Fetch testimonials by star rating
  const getTestimonialsByStars = (stars: number, params?: Omit<TestimonialsParams, 'stars'>) => {
    return useQuery<PaginatedResponse<Testimonial>>({
      queryKey: ['testimonials', 'stars', stars, params],
      queryFn: async () => {
        try {
          const response = await api.get(`/testimonials/stars/${stars}`, { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving testimonials by rating');
          throw error;
        }
      },
      enabled: stars >= 1 && stars <= 5
    });
  };

  // Fetch a specific testimonial by ID
  const getTestimonial = (id: string) => {
    return useQuery<Testimonial>({
      queryKey: ['testimonial', id],
      queryFn: async () => {
        try {
          const response = await api.get(`/testimonials/${id}`);
          return response.data;
        } catch (error) {
          message.error('Error retrieving testimonial details');
          throw error;
        }
      },
      enabled: !!id
    });
  };

  // Fetch testimonial statistics (admin only)
  const getTestimonialStats = () => {
    return useQuery<TestimonialStats>({
      queryKey: ['testimonials', 'stats'],
      queryFn: async () => {
        try {
          const response = await api.get('/testimonials/stats');
          return response.data;
        } catch (error) {
          message.error('Error retrieving testimonial statistics');
          throw error;
        }
      },
      enabled: isAuthenticated
    });
  };

  // Fetch archived testimonials (admin only)
  const getArchivedTestimonials = (params?: TestimonialsParams) => {
    return useQuery<PaginatedResponse<Testimonial>>({
      queryKey: ['testimonials', 'archived', params],
      queryFn: async () => {
        try {
          const response = await api.get('/testimonials/archived', { params });
          return response.data;
        } catch (error) {
          message.error('Error retrieving archived testimonials');
          throw error;
        }
      },
      enabled: isAuthenticated
    });
  };

  // Add a new testimonial (admin only)
  const addTestimonial = useMutation({
    mutationFn: async (testimonial: NewTestimonialInput) => {
      try {
        const formData = new FormData();
        
        // Add all testimonial fields to FormData
        Object.entries(testimonial).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'avatar' && value instanceof File) {
              formData.append('avatar', value);
            } else {
              formData.append(key, value.toString());
            }
          }
        });

        const response = await api.post('/testimonials', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('API error adding testimonial:', error);
        message.error('Error adding testimonial');
        throw error;
      }
    },
    onSuccess: () => {
      message.success('Testimonial added successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    }
  });

  // Update a testimonial (admin only)
  const updateTestimonial = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateTestimonialInput }) => {
      try {
        const formData = new FormData();
        
        // Add all testimonial fields to FormData
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'avatar' && value instanceof File) {
              formData.append('avatar', value);
            } else {
              formData.append(key, value.toString());
            }
          }
        });

        const response = await api.patch(`/testimonials/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        return response.data;
      } catch (error) {
        console.error('API error updating testimonial:', error);
        message.error('Error updating testimonial');
        throw error;
      }
    },
    onSuccess: (_data, variables) => {
      message.success('Testimonial updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonial', variables.id] });
    }
  });

  // Archive a testimonial (admin only)
  const archiveTestimonial = useMutation({
    mutationFn: async (testimonialId: string) => {
      try {
        const response = await api.patch(`/testimonials/${testimonialId}/archive`);
        return response.data;
      } catch (error) {
        message.error('Error archiving testimonial');
        throw error;
      }
    },
    onSuccess: (_, testimonialId) => {
      message.success('Testimonial archived successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonial', testimonialId] });
    }
  });

  // Restore an archived testimonial (admin only)
  const restoreTestimonial = useMutation({
    mutationFn: async (testimonialId: string) => {
      try {
        const response = await api.patch(`/testimonials/${testimonialId}/restore`);
        return response.data;
      } catch (error) {
        message.error('Error restoring testimonial');
        throw error;
      }
    },
    onSuccess: (_, testimonialId) => {
      message.success('Testimonial restored successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonial', testimonialId] });
      queryClient.invalidateQueries({ queryKey: ['testimonials', 'archived'] });
    }
  });

  // Toggle testimonial active/inactive status (admin only)
  const toggleTestimonialStatus = useMutation({
    mutationFn: async (testimonialId: string) => {
      try {
        const response = await api.patch(`/testimonials/${testimonialId}/toggle-status`);
        return response.data;
      } catch (error) {
        message.error('Error toggling testimonial status');
        throw error;
      }
    },
    onSuccess: (data, testimonialId) => {
      message.success(data.message || 'Testimonial status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonial', testimonialId] });
    }
  });

  // Delete a testimonial permanently (admin only)
  const deleteTestimonial = useMutation({
    mutationFn: async (testimonialId: string) => {
      try {
        const response = await api.delete(`/testimonials/${testimonialId}`);
        return response.data;
      } catch (error) {
        message.error('Error deleting testimonial');
        throw error;
      }
    },
    onSuccess: (_, testimonialId) => {
      message.success('Testimonial deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['testimonial', testimonialId] });
    }
  });

  return {
    testimonials,
    getTestimonial,
    getTestimonialsWithFilters,
    getActiveTestimonials,
    getTestimonialsByStars,
    getTestimonialStats,
    getArchivedTestimonials,
    addTestimonial,
    updateTestimonial,
    archiveTestimonial,
    restoreTestimonial,
    toggleTestimonialStatus,
    deleteTestimonial
  };
}; 